import { Panel } from '@bcecloud/portal-ui';

/** A labeled count with a share — the row shape the breakdown panels render. */
export interface BreakdownPanelRow {
  readonly key: string;
  readonly leads: number;
  readonly signed: number;
}

/** Small display pieces shared by the Analytics pages. */

/** A small on/off pill for chart options. */
export function TogglePill({
  on,
  onToggle,
  children,
}: {
  readonly on: boolean;
  readonly onToggle: () => void;
  readonly children: React.ReactNode;
}): React.ReactElement {
  return (
    <button
      type="button"
      aria-pressed={on}
      onClick={onToggle}
      // 40px tall on touch screens: a finger-sized target without growing the desktop pill.
      className={
        on
          ? 'rounded-portal-pill border border-portal-ink bg-portal-ink px-3 py-1 text-portal-micro font-bold text-white pointer-coarse:min-h-10'
          : 'rounded-portal-pill border border-portal-line bg-white px-3 py-1 text-portal-micro text-portal-muted hover:border-portal-ink pointer-coarse:min-h-10'
      }
    >
      {children}
    </button>
  );
}


/** One breakdown: label, count, share bar. */
export function BreakdownPanel({
  title,
  hint,
  rows,
  total,
}: {
  readonly title: string;
  readonly hint?: string;
  readonly rows: readonly BreakdownPanelRow[];
  readonly total: number;
}): React.ReactElement {
  return (
    <Panel className="p-5">
      <h3 className="text-portal-card-title font-bold text-portal-ink">{title}</h3>
      {hint ? <p className="text-portal-micro text-portal-muted">{hint}</p> : null}
      {rows.length === 0 ? (
        <p className="mt-2 text-portal-micro text-portal-muted">Nothing in this range.</p>
      ) : (
        <ul className="mt-3 space-y-2">
          {rows.map((row) => {
            const share = total === 0 ? 0 : Math.round((row.leads / total) * 100);
            return (
              <li key={row.key} className="text-portal-small">
                <div className="flex items-baseline justify-between gap-3">
                  <span className="truncate text-portal-ink">{row.key}</span>
                  <span className="whitespace-nowrap text-portal-muted tabular-nums">
                    {row.leads.toLocaleString()} · {share}%
                    {row.signed > 0 ? ` · ${row.signed} signed` : ''}
                  </span>
                </div>
                <div className="mt-1 h-1.5 w-full rounded-full bg-portal-bg" aria-hidden="true">
                  <div className="h-1.5 rounded-full bg-brand" style={{ width: `${share}%` }} />
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </Panel>
  );
}
